import { useState } from "react"
import { Card, CardContent, Chip, FormControlLabel, Stack, Switch, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If, Else } from "lib/@components/switch"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { DemandChip } from "./demand-chip"

/**
 * The authoritative set, produced by running the real Authorization chain server-side rather
 * than by summing the sections above — so it cannot drift from what the user actually gets.
 *
 * Shows the DELTA by default, because that is the question being asked; the full list is a
 * toggle. Three delta groups, in descending order of how much they warrant a look:
 *
 *   forced  — present only because an override overrules a filter (the serious one)
 *   added   — granted by an override, nothing objected
 *   removed — granted upstream, stripped downstream
 *
 * The pills keep their colours in the full list too, so switching view never loses the delta.
 */
export function FinalSection() {
    const { breakdown } = useBoundContext()
    const [showAll, setShowAll] = useState(false)

    const final = [...(breakdown.final ?? [])].sort()
    const forced = (breakdown.forced ?? []).map(demandOf)
    // A forced demand is also in addedByOverrides; show it once, in the more serious group.
    const added = (breakdown.addedByOverrides ?? []).filter(isNotForced)
    const removed = (breakdown.removed ?? []).map(demandOf)
    const unchanged = final.length - added.length - forced.length
    // Removed demands are by definition NOT in `final`, so the full list has to merge them back
    // in to be a complete picture of everything in play.
    const everything = [...new Set([...final, ...removed])].sort()

    return (
        <Card data-cy="access-final-section">
            <CardContent>
                <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Typography variant="subtitle1">Final state</Typography>
                        <Chip size="small" label={`${final.length} demands`} />
                        <If value={forced.length > 0}>
                            <Chip size="small" color="warning" label={`${forced.length} forced`} />
                        </If>
                        <If value={added.length > 0}>
                            <Chip size="small" color="success" label={`+${added.length} added`} />
                        </If>
                        <If value={removed.length > 0}>
                            <Chip size="small" color="error" variant="outlined" label={`−${removed.length} removed`} />
                        </If>
                        <FormControlLabel
                            sx={{ ml: "auto" }}
                            control={<Switch size="small" checked={showAll} onChange={toggle} />}
                            label={<Typography variant="caption">Show all demands</Typography>}
                            data-cy="final-show-all"
                        />
                    </Stack>

                    <If value={showAll}>
                        <Stack spacing={0.5} data-cy="final-all">
                            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                <Repeat list={everything} item={<DemandChip />} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                {final.length} in effect
                                {forced.length > 0 ? `, ${forced.length} in amber forced past a filter` : ""}
                                {added.length > 0 ? `, ${added.length} in green came from overrides` : ""}
                                {removed.length > 0 ? `, ${removed.length} in red were removed downstream` : ""}.
                            </Typography>
                        </Stack>
                        <Else>
                            <Stack spacing={1} data-cy="final-delta">
                                <If value={forced.length > 0}>
                                    <Stack spacing={0.5}>
                                        <Typography variant="caption" color="warning.main">
                                            Forced through &mdash; would have been removed without an override
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                            <Repeat list={forced} item={<DemandChip />} />
                                        </Stack>
                                    </Stack>
                                </If>
                                <If value={added.length > 0}>
                                    <Stack spacing={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            Added by overrides
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                            <Repeat list={added} item={<DemandChip />} />
                                        </Stack>
                                    </Stack>
                                </If>
                                <If value={removed.length > 0}>
                                    <Stack spacing={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            Removed downstream
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                            <Repeat list={removed} item={<DemandChip />} />
                                        </Stack>
                                    </Stack>
                                </If>
                                <If value={forced.length === 0 && added.length === 0 && removed.length === 0}>
                                    <Typography variant="body2" color="text.secondary">
                                        No difference between what was granted and what applies — all {final.length}{" "}
                                        demands come through unchanged.
                                    </Typography>
                                    <Else>
                                        <Typography variant="caption" color="text.secondary">
                                            {unchanged} further demand{unchanged === 1 ? "" : "s"} unchanged. Toggle
                                            above to see the full list.
                                        </Typography>
                                    </Else>
                                </If>
                            </Stack>
                        </Else>
                    </If>
                </Stack>
            </CardContent>
        </Card>
    )

    function toggle(e) {
        setShowAll(e.target.checked)
    }

    function isNotForced(demand) {
        return !forced.includes(demand)
    }
}

function demandOf(entry) {
    return entry.demand
}
